import { Alert } from '@creditornot/cb-alert';
import { Button, TextButton } from '@creditornot/cb-button';
import { Checkbox } from '@creditornot/cb-checkbox';
import { colors, cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { Spinner } from '@creditornot/cb-progress';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { parse } from 'papaparse';
import { uniq } from 'ramda';
import React, { useCallback, useContext, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import { readDeposits } from 'apis/restaurant-api/merchant-admin/deposits/api';
import { getProductInformationSettings } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Category, Item } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { APIErrorNotification, WoltModal } from 'common/components';
import { CurrencyContext } from 'common/formatting/CurrencyContext';
import { isDefined } from 'common/utils';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import { MenuMetaContext } from 'modules/menu/MenuMetaContext';
import { MenuPermissionsContext } from 'modules/menu/components/MenuPermissionsContext';
import { trackSelectCSVForItemUpdate, trackUpdateItemsFromCsv } from 'modules/menu/hooks/telemetryHooks';

import {
  asyncValidateCsvResults,
  mapValidationErrors,
  getUpdatePayloadsFromCsvData,
  CsvFileError,
  getCsvSettingsData,
  submitHandler,
} from './utils';
import { ValidCSVData } from './validations';
import { MenuFeaturesContext } from '../../components/MenuFeaturesContext';
import CsvFileDropzone from '../CsvFileDropzone';
import { FileSelectedProps, GoogleModeSwitchButton, GoogleSelectZone } from '../GoogleSheetsSelectZone';
import { csvColumnsTranslationMap } from '../constants';
import useAvailableColumnKeys from '../hooks/useAvailableColumnKeys';
import { CSVColumnKey } from '../types';
import { useIsGoogleSheetsEnabled } from '../useIsGoogleSheetsEnabled';
import { downloadItemsAsCsv, downloadUnmatchedDataAsCsv, normalizeEncoding, parseDelimiter } from '../utils';

type Props = {
  isOpen: boolean;
  items: Item[];
  categories: Category[];
  language: Language;
  venueName: string;
  onUpload: () => void;
  onClose: (reloadMenu?: boolean) => void;
  onSpreadsheetSelectStart?: () => void;
  onSpreadsheetSelectEnd?: () => void;
};

const StyledWoltModal = styled(WoltModal)`
  min-width: 640px;
  max-width: 800px;
  background: ${cbSemanticCssVars.surfaceMain};
  color: ${cbSemanticCssVars.textPrimary};
  border-radius: 8px;
`;

const StyledModalContent = styled(ModalContent)`
  ${typographyCss.Body3()};
  max-height: 480px;
  overflow: auto;
`;

const SpinnerContainer = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ErrorList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ErrorListItem = styled.li`
  margin: 0;
  padding: 0;
`;

const StepContainer = styled.div`
  margin-block-start: 16px;
  display: flex;
  justify-content: space-between;
`;

const CheckboxesContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
`;

const StyledCheckbox = styled(Checkbox)`
  ${typographyCss.Body3()};
  margin-block-start: 16px;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ProgressContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  margin-inline-end: 16px;
`;

const ProgressBar = styled.div<{ progress: number }>`
  position: relative;
  height: 8px;
  width: 100%;
  border-radius: 4px;
  background-color: ${cssThemedValue({ light: colors.pepper12, dark: colors.salt8 })};
  &::after {
    position: absolute;
    left: 0;
    top: 0;
    content: '';
    height: 8px;
    width: ${({ progress }) => progress}%;
    border-radius: 4px;
    background-color: ${colors.lime100};
    transition: width 100ms ease;
  }
`;

const StyledAPIErrorNotification = styled(APIErrorNotification)`
  margin-block-start: 16px;
`;

const ToastInnerTitle = styled.div`
  font-weight: 600;
`;

type ErrorState =
  | { type: 'validation'; error: yup.ValidationError[] }
  | { type: 'file'; error: CsvFileError }
  | { type: 'generic'; error: any };

const CSVItemDetailsUploadModal = ({
  isOpen,
  items,
  categories,
  language,
  venueName,
  onUpload,
  onClose,
  onSpreadsheetSelectStart = () => null,
  onSpreadsheetSelectEnd = () => null,
}: Props) => {
  const isGoogleSheetsEnabled = useIsGoogleSheetsEnabled();
  const intl = useIntl();

  const { menuId, venueId, merchantId } = useContext(MenuMetaContext);
  const { overTheCounter, bottleDeposits } = useContext(MenuFeaturesContext);
  const { currency } = useContext(CurrencyContext);
  const { customCommissionRead } = useContext(MenuPermissionsContext);
  const { country } = useContext(MenuLocaleContext);

  const [csvFileName, setCsvFileName] = useState<string>();
  const [parsing, setParsing] = useState(false);
  const [submitPercentage, setSubmitPercentage] = useState(0);
  const [googleSheetsMode, setGoogleSheetsMode] = useState<boolean>(false);

  const [isSpreadsheetLoading, setIsSpreadsheetLoading] = useState(false);
  const [validCsvData, setValidCsvData] = useState<ValidCSVData[]>();
  const [errorState, setErrorState] = useState<ErrorState>();
  const [selectedSpreadSheet, setSelectedSpreadsheet] = useState<FileSelectedProps>();

  const { availableCsvColumns, selectedColumns, initializeColumnsForUpload, selectColumns, deselectColumns } =
    useAvailableColumnKeys('upload');

  const csvColumnsWithItemId: CSVColumnKey[] = useMemo(
    () => uniq(['item_id', ...availableCsvColumns]),
    [availableCsvColumns],
  );

  const {
    data: { product_hierarchy_levels: productHierarchyLevels } = {
      nutrition_values: [],
      product_hierarchy_levels: [],
    },
    isLoading: loadingInfoSettings,
    error: errorSettings,
  } = useQuery([`product-information-settings/${menuId}`], () => getProductInformationSettings(menuId), {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { data: deposits } = useQuery(
    ['v1/merchant-admin/deposit_permutations', country],
    () => readDeposits(country),
    {
      refetchOnMount: false,
      enabled: bottleDeposits,
    },
  );

  const downloadFailedItems = useCallback(
    async (errorData: { [itemId: string]: (string | undefined)[] }) => {
      const failedItems = Object.keys(errorData)
        .map(itemId => items.find(item => item.id === itemId))
        .filter(isDefined);
      const csvSettingsData = await getCsvSettingsData(
        menuId,
        venueId,
        failedItems,
        csvColumnsWithItemId,
        customCommissionRead,
      );

      downloadItemsAsCsv(
        failedItems,
        csvColumnsWithItemId,
        `${venueName}-failed-items-${DateTime.local().toISODate()}.csv`,
        {
          categories,
          language,
          deposits: deposits ?? [],
          ...csvSettingsData,
          errorData,
        },
      );
    },
    [categories, csvColumnsWithItemId, customCommissionRead, deposits, items, language, menuId, venueId, venueName],
  );

  const downloadFailedRows = useCallback(
    (failedRows: ValidCSVData[]) => {
      downloadUnmatchedDataAsCsv(failedRows, csvColumnsWithItemId, venueName, language === 'ja');
    },
    [csvColumnsWithItemId, language, venueName],
  );

  const { mutate: handleSubmit, isLoading: submitting } = useMutation(
    async () => {
      if (!validCsvData) {
        throw new Error('No CSV data found, this should not happen!');
      }

      const csvSettingsData = await getCsvSettingsData(menuId, venueId, items, selectedColumns, customCommissionRead);

      downloadItemsAsCsv(
        items,
        uniq(['item_id', ...selectedColumns]),
        `${venueName}-backup-${DateTime.local().toISO({ includeOffset: false }).replace(/:/g, '.')}.csv`,
        {
          categories,
          language,
          deposits,
          ...csvSettingsData,
        },
      );

      const { payloads, unmatchedData } = getUpdatePayloadsFromCsvData(validCsvData, items, {
        selectedFields: selectedColumns,
        categories,
        language,
        deposits,
        ...csvSettingsData,
        menuId,
      });

      const errorData = await submitHandler(merchantId, venueId, menuId, payloads, setSubmitPercentage);

      if (unmatchedData.length > 0) {
        toast(
          <>
            <FormattedMessage id="menu.csvItemUpdate.itemNotFoundForRows" />
            <TextButton
              variant="brand"
              onClick={() => downloadFailedRows(unmatchedData)}
              style={{ marginBlockStart: 8 }}
            >
              <FormattedMessage id="menu.csvItemUpdate.downloadFailedRows" />
            </TextButton>
          </>,
          {
            duration: Infinity,
          },
        );
      }

      const isEveryPayloadEmpty = payloads.every(payload => payload.length === 0);

      if (isEveryPayloadEmpty) {
        toast(intl.formatMessage({ id: 'menu.csvItemUpdate.allItemsUnchanged' }), { duration: Infinity });
      }
      if (Object.keys(errorData).length > 0) {
        toast(
          <>
            <ToastInnerTitle>
              <FormattedMessage id="menu.csvItemUpdate.updateFailed" />
            </ToastInnerTitle>
            <TextButton variant="brand" onClick={() => downloadFailedItems(errorData)} style={{ marginBlockStart: 8 }}>
              <FormattedMessage id="menu.csvItemUpdate.downloadFailedRows" />
            </TextButton>
          </>,
          { duration: Infinity },
        );
      }
      if (!isEveryPayloadEmpty && Object.keys(errorData).length === 0) {
        toast.success(intl.formatMessage({ id: 'menu.csvItemUpdate.allUpdatesSucceeded' }));
      }

      onUpload();
      onClose(!isEveryPayloadEmpty);
    },
    {
      onSuccess: () => trackUpdateItemsFromCsv('Success', selectedColumns),
      onError: error => {
        setErrorState({ type: 'generic', error });
        trackUpdateItemsFromCsv(`Failure: ${error}`, selectedColumns);
      },
    },
  );

  const overTheCounterProductHierarchies = useMemo(
    () =>
      productHierarchyLevels
        .filter(level => level.tags.includes('over_the_counter'))
        .map(({ human_readable_id }) => human_readable_id),
    [productHierarchyLevels],
  );

  const areAllFieldsSelected = selectedColumns.length === availableCsvColumns.length;

  const parseCsv = async (file: File) => {
    const delimiter = await parseDelimiter(file);
    const normalizedFile = await normalizeEncoding(file);
    parse<Record<CSVColumnKey, string | undefined>>(normalizedFile, {
      header: true,
      skipEmptyLines: true,
      delimiter,
      transform: value => (value === '' ? undefined : value),
      complete: async results => {
        try {
          const { validData, fields } = await asyncValidateCsvResults(
            results,
            {
              currency,
              overTheCounterProductHierarchies,
              categories,
            },
            { overTheCounter },
          );

          initializeColumnsForUpload(fields);
          setValidCsvData(validData);
          trackSelectCSVForItemUpdate('Success');
        } catch (error) {
          if (error instanceof yup.ValidationError) {
            setErrorState({ type: 'validation', error: error.inner });
          } else if (error instanceof CsvFileError) {
            setErrorState({ type: 'file', error });
          } else {
            setErrorState({ type: 'generic', error });
          }
          trackSelectCSVForItemUpdate(`Failure: ${error}`);
        }
        setParsing(false);
      },
    });
  };

  const onGoogleSpreadsheetSelected = ({ file, result, data }: FileSelectedProps) => {
    setValidCsvData(undefined);
    setSelectedSpreadsheet({ file, result, data });
    setIsSpreadsheetLoading(true);
    parseCsv(file);
    setIsSpreadsheetLoading(false);
  };

  return (
    <StyledWoltModal isOpen={isOpen} onRequestClose={() => onClose()}>
      <ModalHeader onCloseClick={() => onClose()}>
        <FormattedMessage
          id={isGoogleSheetsEnabled ? 'menu.csvItemUpdate.updateItems' : 'menu.csvItemUpdate.updateItemsFromCsv'}
        />
      </ModalHeader>
      {isGoogleSheetsEnabled && <GoogleModeSwitchButton value={googleSheetsMode} onChange={setGoogleSheetsMode} />}
      <StyledModalContent>
        {loadingInfoSettings && (
          <SpinnerContainer>
            <Spinner size="large" />
          </SpinnerContainer>
        )}

        {!loadingInfoSettings && !errorSettings && (
          <>
            <FormattedMessage
              id="menu.csvItemUpdate.uploadCsv"
              values={{ step: chunks => <strong>{chunks}</strong> }}
            />
            {googleSheetsMode ? (
              <GoogleSelectZone
                hasError={errorState?.type === 'validation' || errorState?.type === 'file'}
                loading={isSpreadsheetLoading}
                onFileSelected={onGoogleSpreadsheetSelected}
                onStartSelect={onSpreadsheetSelectStart}
                onEndSelect={onSpreadsheetSelectEnd}
                fileSelected={selectedSpreadSheet ?? undefined}
              />
            ) : (
              <CsvFileDropzone
                fileName={csvFileName}
                hasError={errorState?.type === 'validation' || errorState?.type === 'file'}
                loading={parsing}
                onDrop={async files => {
                  setErrorState(undefined);
                  setValidCsvData(undefined);
                  setParsing(true);
                  setCsvFileName(files[0].name);
                  if (!files[0].name.endsWith('.csv')) {
                    const error = new CsvFileError({ id: 'menu.csvItemImport.fileMustBeCsv' });
                    setErrorState({ type: 'file', error });
                    setParsing(false);
                    trackSelectCSVForItemUpdate(`Failure: wrong file extension ${files[0].name}`);
                    return;
                  }
                  parseCsv(files[0]);
                }}
              />
            )}

            {errorState?.type === 'file' && (
              <InputAlert style={{ marginBlockStart: 8 }} variant="error">
                <FormattedMessage id={errorState.error.id} values={errorState.error.values} />
              </InputAlert>
            )}
            {errorState?.type === 'validation' && (
              <Alert
                style={{ marginBlockStart: 16 }}
                variant="error"
                title={intl.formatMessage({ id: 'menu.csvItemUpdate.errorsTitle' }, { count: errorState.error.length })}
                showMoreContent={
                  <ErrorList>
                    {errorState.error.map(mapValidationErrors).map(error => (
                      <ErrorListItem key={JSON.stringify(error)} style={{ marginBlockEnd: 8 }}>
                        <FormattedMessage id="menu.csvItemPriceUpdate.rowLabel" values={{ row: error.row || 'N/A' }} />:{' '}
                        {error.column && `"${error.column}". `}
                        <FormattedMessage id={error.message.id} values={error.message?.values} />
                      </ErrorListItem>
                    ))}
                  </ErrorList>
                }
              />
            )}
            {validCsvData && (
              <>
                <StepContainer>
                  <div>
                    <FormattedMessage
                      id="menu.csvItemUpdate.selectFields"
                      values={{ step: chunks => <strong>{chunks}</strong> }}
                    />
                  </div>
                  <TextButton
                    variant="brand"
                    onClick={() =>
                      areAllFieldsSelected ? deselectColumns(selectedColumns) : selectColumns(availableCsvColumns)
                    }
                  >
                    <FormattedMessage
                      id={areAllFieldsSelected ? 'menu.csvItemUpdate.deselectAll' : 'menu.csvItemUpdate.selectAll'}
                    />
                  </TextButton>
                </StepContainer>
                <CheckboxesContainer>
                  {availableCsvColumns.map(key => (
                    <StyledCheckbox
                      key={key}
                      checked={selectedColumns.includes(key)}
                      disabled={submitting}
                      onChange={event => (event.currentTarget.checked ? selectColumns([key]) : deselectColumns([key]))}
                    >
                      <FormattedMessage id={csvColumnsTranslationMap[key]} />
                    </StyledCheckbox>
                  ))}
                </CheckboxesContainer>
              </>
            )}
          </>
        )}
        {!!errorSettings && <StyledAPIErrorNotification error={errorSettings} />}
      </StyledModalContent>

      <StyledModalFooter>
        {errorState?.type === 'generic' && (
          <APIErrorNotification style={{ marginBlockEnd: 16 }} error={errorState.error} />
        )}
        <ButtonsContainer>
          <ProgressContainer>{submitting && <ProgressBar progress={submitPercentage} />}</ProgressContainer>
          <Button size="small" variant="secondary" disabled={submitting} onClick={() => onClose()}>
            <FormattedMessage id="state.cancel" />
          </Button>
          <Button
            style={{ marginInlineStart: 16 }}
            size="small"
            variant="primary"
            disabled={selectedColumns.length === 0 || submitting}
            loading={submitting}
            onClick={() => handleSubmit()}
          >
            <FormattedMessage id="menu.csvItemUpdate.downloadAndSubmit" />
          </Button>
        </ButtonsContainer>
      </StyledModalFooter>
    </StyledWoltModal>
  );
};

export default CSVItemDetailsUploadModal;
